import type { EventBusKey } from '@vueuse/core'

export type TRecruitmentCandidateCompletionFieldKey =
  | 'firstname'
  | 'lastname'
  | 'email'
  | 'cv'
  | 'jobCategory'
  | 'position'
  | 'source'
  | 'office'
  | 'team'
  | 'contractType'
  | 'group'
  | 'phoneNumber1'
  | 'gender'
  | 'picture'
  | 'degrees'
  | 'availabilityDate'
  | 'managedBy'
  | 'plannedContractStartDate'
  | 'evaluationForm'
  | 'tags'
  | 'dataDuration'
  | 'isConfidential'
  | 'priority'
  | 'resourceType'

export type TRecruitmentCandidateCreationFieldKey = TRecruitmentCandidateCompletionFieldKey
export type TRecruitmentCandidateProfileFieldKey = TRecruitmentCandidateCompletionFieldKey
export type TRecruitmentCandidateAcceptOfferFieldKey = TRecruitmentCandidateCompletionFieldKey

export const recruitmentCandidateFieldSettingsColumns = [
  'display',
  'creation',
  'profile',
  'acceptOffer',
] as const

export type TCandidateFieldSettingsColumn = typeof recruitmentCandidateFieldSettingsColumns[number]

export type TRecruitmentFieldDefinition<TKey extends string = string> = {
  key: TKey
  labelKey: string
  /** When false, the field is always shown in create/recommendation forms. */
  configurable?: boolean
  /**
   * Settings matrix columns where this field can be toggled.
   * Defaults to all columns when omitted.
   */
  settingsColumns?: readonly TCandidateFieldSettingsColumn[]
}

export type TRecruitmentFieldState<TKey extends string = string> = TRecruitmentFieldDefinition<TKey> & {
  required: boolean
  filled: boolean
}

export const defaultRecruitmentCandidateCompletionFieldKeys: TRecruitmentCandidateCompletionFieldKey[] = [
  'firstname',
  'lastname',
  'email',
  'position',
  'source',
  'cv',
  'office',
  'team',
  'contractType',
  'group',
  'phoneNumber1',
  'gender',
  'degrees',
  'availabilityDate',
  'managedBy',
  'picture',
  'plannedContractStartDate',
  'evaluationForm',
  'tags',
  'dataDuration',
  'isConfidential',
  'priority',
  'resourceType',
]

export const defaultRecruitmentCandidateCreationFieldKeys: TRecruitmentCandidateCompletionFieldKey[] = [
  'firstname',
  'lastname',
  'email',
  'position',
  'source',
  'cv',
  'office',
]

// Always shown and required, at every step (mirrored in apps/api/api/office/recruitmentCandidateFields.js)
export const recruitmentCandidateAlwaysVisibleFieldKeys: TRecruitmentCandidateCompletionFieldKey[] = [
  'firstname',
  'lastname',
  'email',
  'cv',
  'office',
]

// The grade category is shown by default, but required nowhere until an office asks for it
export const defaultRecruitmentCandidateFormVisibleFieldKeys: TRecruitmentCandidateCompletionFieldKey[] = [
  ...defaultRecruitmentCandidateCompletionFieldKeys,
  'jobCategory',
]

/** Defaults that only make sense as creation-form visibility (always defined / not "required" elsewhere). */
const creationDisplayOnlySettingsColumns = ['display'] as const satisfies readonly TCandidateFieldSettingsColumn[]

/** Fields relevant only during candidate creation (visibility + required), not profile / accept-offer. */
const creationOnlySettingsColumns = ['display', 'creation'] as const satisfies readonly TCandidateFieldSettingsColumn[]

export const recruitmentCandidateCompletionFieldDefinitions: TRecruitmentFieldDefinition<TRecruitmentCandidateCompletionFieldKey>[] = [
  { key: 'firstname', labelKey: 'modules.recruitment.createCandidate.mandatoryInformation.firstname', configurable: false },
  { key: 'lastname', labelKey: 'modules.recruitment.createCandidate.mandatoryInformation.lastname', configurable: false },
  { key: 'email', labelKey: 'modules.recruitment.createCandidate.mandatoryInformation.email', configurable: false },
  { key: 'cv', labelKey: 'modules.recruitment.createCandidate.mandatoryInformation.cv', configurable: false },
  { key: 'jobCategory', labelKey: 'modules.recruitment.createCandidate.mandatoryInformation.jobCategory', configurable: true },
  { key: 'position', labelKey: 'modules.recruitment.createCandidate.mandatoryInformation.targetPosition', configurable: true },
  { key: 'source', labelKey: 'modules.recruitment.createCandidate.optionalInformation.applicationSource', configurable: true },
  { key: 'office', labelKey: 'modules.recruitment.createCandidate.optionalInformation.office', configurable: false },
  { key: 'team', labelKey: 'modules.recruitment.createCandidate.optionalInformation.team', configurable: true },
  { key: 'contractType', labelKey: 'modules.recruitment.createCandidate.optionalInformation.contract', configurable: true },
  { key: 'group', labelKey: 'modules.recruitment.createCandidate.optionalInformation.group', configurable: true },
  { key: 'phoneNumber1', labelKey: 'modules.recruitment.createCandidate.optionalInformation.phone', configurable: true },
  { key: 'picture', labelKey: 'modules.recruitment.settings.recruitmentOffices.candidatePicture', configurable: true },
  { key: 'gender', labelKey: 'modules.recruitment.recommandation.mandatoryInformation.gender', configurable: true },
  { key: 'degrees', labelKey: 'modules.recruitment.settings.recruitmentOffices.degrees', configurable: true },
  { key: 'availabilityDate', labelKey: 'modules.recruitment.screening.editCandidateInformations.recruitmentProcess.availabilityDate', configurable: true },
  { key: 'managedBy', labelKey: 'modules.recruitment.settings.recruitmentOffices.managedBy', configurable: true },
  { key: 'plannedContractStartDate', labelKey: 'modules.recruitment.screening.editCandidateInformations.recruitmentProcess.plannedContractStartDate', configurable: true },
  {
    key: 'evaluationForm',
    labelKey: 'modules.recruitment.createCandidate.optionalInformation.typeEvaluation',
    configurable: true,
    settingsColumns: creationOnlySettingsColumns,
  },
  { key: 'tags', labelKey: 'modules.recruitment.tags.label', configurable: true },
  { key: 'dataDuration', labelKey: 'modules.recruitment.screening.editCandidateInformations.optionalFields.dataRetention', configurable: true },
  {
    key: 'isConfidential',
    labelKey: 'modules.recruitment.screening.editCandidateInformations.optionalFields.evaluationVisibility',
    configurable: true,
    settingsColumns: creationDisplayOnlySettingsColumns,
  },
  { key: 'priority', labelKey: 'modules.recruitment.screening.editCandidateInformations.optionalFields.priority', configurable: true },
  {
    key: 'resourceType',
    labelKey: 'modules.recruitment.screening.editCandidateInformations.optionalFields.resourceType',
    configurable: true,
    settingsColumns: creationDisplayOnlySettingsColumns,
  },
]

export const isRecruitmentFieldConfigurableInSettingsColumn = (
  fieldKey: TRecruitmentCandidateCompletionFieldKey,
  column: TCandidateFieldSettingsColumn,
): boolean => {
  const definition = recruitmentCandidateCompletionFieldDefinitions.find(field => field.key === fieldKey)
  if (!definition) return false
  const columns = definition.settingsColumns ?? recruitmentCandidateFieldSettingsColumns
  return columns.includes(column)
}

export const defaultRecruitmentCandidateProfileFieldKeys: TRecruitmentCandidateCompletionFieldKey[]
  = defaultRecruitmentCandidateCompletionFieldKeys.filter(key =>
    isRecruitmentFieldConfigurableInSettingsColumn(key, 'profile'),
  )
export const defaultRecruitmentCandidateAcceptOfferFieldKeys: TRecruitmentCandidateCompletionFieldKey[]
  = defaultRecruitmentCandidateCompletionFieldKeys.filter(key =>
    isRecruitmentFieldConfigurableInSettingsColumn(key, 'acceptOffer'),
  )

export const recruitmentCandidateCreationFieldDefinitions: TRecruitmentFieldDefinition<TRecruitmentCandidateCompletionFieldKey>[] = recruitmentCandidateCompletionFieldDefinitions
export const recruitmentCandidateProfileFieldDefinitions: TRecruitmentFieldDefinition<TRecruitmentCandidateCompletionFieldKey>[] = recruitmentCandidateCompletionFieldDefinitions
export const recruitmentCandidateAcceptOfferFieldDefinitions: TRecruitmentFieldDefinition<TRecruitmentCandidateCompletionFieldKey>[] = recruitmentCandidateCompletionFieldDefinitions

export const recruitmentCandidateFormVisibilityFieldDefinitions = recruitmentCandidateCompletionFieldDefinitions.filter(
  field => field.configurable !== false,
)

export const resolveRecruitmentConfiguredFieldKeys = <TKey extends string>(
  configuredFields: TKey[] | null | undefined,
  defaultFields: TKey[],
  settingsColumn?: TCandidateFieldSettingsColumn,
): TKey[] => {
  const keys = configuredFields == null ? defaultFields : configuredFields
  if (!settingsColumn) return keys

  return keys.filter(key =>
    isRecruitmentFieldConfigurableInSettingsColumn(
      key as TRecruitmentCandidateCompletionFieldKey,
      settingsColumn,
    ),
  )
}

export const resolveRecruitmentFormVisibleFieldKeys = (
  visibleFields?: TRecruitmentCandidateCompletionFieldKey[] | null,
): TRecruitmentCandidateCompletionFieldKey[] => (
  resolveRecruitmentConfiguredFieldKeys(visibleFields, defaultRecruitmentCandidateFormVisibleFieldKeys)
)

export const isRecruitmentFieldVisible = (
  fieldKey: TRecruitmentCandidateCompletionFieldKey,
  visibleFields?: TRecruitmentCandidateCompletionFieldKey[] | null,
): boolean => {
  const definition = recruitmentCandidateCompletionFieldDefinitions.find(field => field.key === fieldKey)
  if (definition?.configurable === false) return true

  return resolveRecruitmentFormVisibleFieldKeys(visibleFields).includes(fieldKey)
}

export const isRecruitmentFieldFilled = (value: unknown): boolean => {
  if (Array.isArray(value)) return value.length > 0
  if (value instanceof Date) return true
  if (typeof value === 'number') return !Number.isNaN(value)
  if (typeof value === 'boolean') return true

  return value !== null && value !== undefined && value !== ''
}

const getRecruitmentCandidateFieldValue = (candidate: any, process: any, fieldKey: TRecruitmentCandidateCompletionFieldKey) => {
  switch (fieldKey) {
    case 'firstname':
      return candidate?.firstname
    case 'lastname':
      return candidate?.lastname
    case 'email':
      return candidate?.user?.email
    case 'jobCategory':
      return process?.jobCategory?._id ?? process?.jobCategoryId ?? null
    case 'position':
      return process?.position?._id ?? process?.positionId ?? null
    case 'source':
      return process?.candidateSourcePopulated?._id ?? process?.candidateSource ?? null
    case 'cv':
      // Creation sends base64 on process.cv; persisted process stores the file on resumePath (see recruitmentProcess.model.js).
      return process?.cv ?? process?.resumePath?.filePath ?? null
    case 'office':
      return process?.office?._id ?? process?.officeId?._id ?? process?.officeId ?? null
    case 'team':
      return process?.team?._id ?? process?.teamId ?? null
    case 'contractType':
      return process?.contractTypePopulated?._id ?? process?.contractType ?? null
    case 'group':
      return candidate?.userGroupsPopulated?.length ? candidate.userGroupsPopulated : candidate?.userGroups
    case 'phoneNumber1':
      return candidate?.phoneNumber1
    case 'gender':
      return candidate?.gender
    case 'picture':
      return candidate?.candidatePicture ?? candidate?.picture ?? candidate?.base64Picture
    case 'degrees':
      return candidate?.degrees
    case 'availabilityDate':
      return candidate?.recruitmentDate
    case 'managedBy':
      return process?.managedBy?.length ? process.managedBy : process?.managedByPopulated
    case 'plannedContractStartDate':
      return process?.plannedContractStartDate
    case 'evaluationForm':
      return process?.evaluationFormPopulated?._id ?? process?.evaluationForm ?? null
    case 'tags':
      return process?.tagsPopulated?.length ? process.tagsPopulated : process?.tags
    case 'dataDuration':
      return candidate?.dataDuration
    case 'isConfidential':
      return process?.isConfidential
    case 'priority':
      return process?.priority
    case 'resourceType':
      return candidate?.resourceType
    default:
      return null
  }
}

export const getRecruitmentCreationFieldValue = (candidate: any, process: any, fieldKey: TRecruitmentCandidateCompletionFieldKey) =>
  getRecruitmentCandidateFieldValue(candidate, process, fieldKey)

export const getRecruitmentProfileFieldValue = (candidate: any, process: any, fieldKey: TRecruitmentCandidateCompletionFieldKey) =>
  getRecruitmentCandidateFieldValue(candidate, process, fieldKey)

export const getRecruitmentAcceptOfferFieldValue = (candidate: any, process: any, fieldKey: TRecruitmentCandidateCompletionFieldKey) =>
  getRecruitmentCandidateFieldValue(candidate, process, fieldKey)

export const buildRecruitmentFieldStates = <TKey extends string>(
  definitions: TRecruitmentFieldDefinition<TKey>[],
  requiredKeys: Set<TKey>,
  getValue: (fieldKey: TKey) => unknown,
): TRecruitmentFieldState<TKey>[] => {
  return definitions.map(definition => ({
    ...definition,
    required: requiredKeys.has(definition.key),
    filled: isRecruitmentFieldFilled(getValue(definition.key)),
  }))
}

export type TCandidateFieldConfiguration = {
  creationRequiredFields: TRecruitmentCandidateCompletionFieldKey[]
  profileCompletionFields: TRecruitmentCandidateCompletionFieldKey[]
  acceptOfferFields: TRecruitmentCandidateCompletionFieldKey[]
  formVisibleFields: TRecruitmentCandidateCompletionFieldKey[]
}

export type TOfficeCandidateFields = {
  _id: string
  display: string
  countryId?: string | null
  candidateFields: TCandidateFieldConfiguration
}

/** Sent once the candidate fields of an office are saved: the office settings keep them when they save that office. */
export const candidateFieldsSavedKey: EventBusKey<TOfficeCandidateFields> = Symbol('candidateFieldsSaved')
